<script lang="ts">
    // Folha de agendamento (v4, mobile). Mesma lógica de gravação do formulário atual
    // (appointment-form.svelte) — o que muda é a apresentação: blocos sóbrios em pilha e o botão de
    // salvar fixo no rodapé. O formulário antigo continua valendo no desktop e nas outras telas que o
    // usam (ficha da pessoa, saldo do cliente, estoque, janela destacada).
    import { createEventDispatcher, onMount } from 'svelte'
    import Fa from 'svelte-fa'
    import { faPlus, faUser } from '@fortawesome/free-solid-svg-icons'
    import { slide } from 'svelte/transition'
    import dayjs from 'dayjs'
    import { Appointment } from '../../../modules/schedulerApi/appointments/appointment'
    import { AppointmentRepeat } from '../../../modules/schedulerApi/appointments/appointment-repeat'
    import { AppointmentStatus } from '../../../modules/schedulerApi/appointments/appointments.enum'
    import type { CreateAppointmentDto } from '../../../modules/schedulerApi/appointments/dto/create-appointment.dto'
    import type { UpdateAppointmentDto } from '../../../modules/schedulerApi/appointments/dto/upate-appointment.dto'
    import type { Schedule } from '../../../modules/schedulerApi/schedules/schedule'
    import type { Person } from '../../../modules/eventsApi/persons/person'
    import type { FormDataLastEmissionDto } from '../../../modules/toolsApi/forms/form-datas/dto/form-data-last-emission.dto'
    import { User } from '../../../modules/usersApi/users/user'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import Popover from '../../_common/popover.svelte'
    import AppointmentFormRequiredDocuments from '../appointment-form-required-documents.svelte'
    import ZentitySelect from '../../_common/z/zentity-select.svelte'
    import Zsheet from '../../_common/z/sheet/zsheet.svelte'
    import ZsheetSection from '../../_common/z/sheet/zsheet-section.svelte'
    import ZsheetEntity from '../../_common/z/sheet/zsheet-entity.svelte'
    import AppointmentCard from './appointment-card.svelte'
    import { intervaloInicial } from '../appointment-dates'

    const dispatch = createEventDispatcher()

    export let appointmentId: string = null
    export let personId: string = null
    export let dates: {
        start: Date
        end: Date
        scheduleId: string
        auto: boolean
        items?: { serviceId: string; userId?: string; parentServicesOnInvoicesId?: string }[]
    } = null

    let entities: Appointment[] = []
    let snapshots: { isDirty: boolean; snapshot: string }[] = []
    let person: Person = null
    let comissionSource = null
    let schedules: Schedule[] = []
    let noPaid: number = 0
    let pendingDocuments: FormDataLastEmissionDto[] = []
    let show = false
    let state: 'ready' | 'typing' = 'ready'

    // mesmo prefixo usado em services.svelte para separar templates v2 dos v1
    const V2_DOCUMENT_PREFIX = 'v2:'

    async function loadSchedules() {
        schedules = await window.api.schedulers.schedules.getAll()
        schedules = schedules.filter((el) => el.status == 100)
    }

    async function loadInvoices(_personId: string) {
        noPaid = 0
        if (!_personId) return
        noPaid = await window.api.pos.invoices.getCreditAndDebitsFromPerson(_personId)
    }

    async function checkRequiredDocument(entity: Appointment) {
        pendingDocuments = []
        const formTemplateIds = []
        for (const item of entity.items) {
            if (!item.service?.documents) continue
            for (const id of item.service.documents) {
                if (formTemplateIds.some((el) => el === id)) continue
                formTemplateIds.push(id)
            }
        }
        if (formTemplateIds.length > 0) {
            const v1Ids = formTemplateIds.filter((id) => !id.startsWith(V2_DOCUMENT_PREFIX))
            const v2Ids = formTemplateIds.filter((id) => id.startsWith(V2_DOCUMENT_PREFIX)).map((id) => id.slice(V2_DOCUMENT_PREFIX.length))
            const [v1Emissions, v2Emissions] = await Promise.all([
                v1Ids.length > 0 ? window.api.tools.forms.formDatas.findLastEmissionsFromPerson(entity.personId, v1Ids) : [],
                v2Ids.length > 0 ? window.api.tools.formsV2.forms.findLastEmissionsFromPerson(entity.personId, v2Ids) : [],
            ])
            pendingDocuments = [...v1Emissions, ...v2Emissions].filter((el) => el.emited === false || el.status < 100)
        }
        return pendingDocuments.length > 0
    }

    async function loadEntity(id: string) {
        entities = []
        snapshots = []
        try {
            show = true
            let entity = await window.api.schedulers.appointments.getOne(id)
            entity.start = new Date(entity.start)
            entity.end = new Date(entity.end)
            entity = await assignObjects(entity)
            for (const item of entity.items) {
                item.label = item.service?.name || '[serviço não cadastrado]'
                if (!item.user) {
                    item.user = new User()
                    item.user.userId = item?.userId
                    item.user.userName = item?.userId ? '[usuário excluído]' : '[usuário não informado]'
                }
            }
            if (!person) person = entity.person
            entities = [entity]
            snapshots = [{ snapshot: JSON.stringify(entity), isDirty: false }]
            if (!dates) dates = { start: entity.start, end: entity.end, scheduleId: entity.scheduleId, auto: entity.auto }
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function assignObjects(entity) {
        entity.items = await window.api.utils.utils.assignUsersFromObject(entity.items)
        entity.items = await window.api.utils.utils.assignServicesFromObject(entity.items, undefined, undefined, true)
        entity.consumption = await window.api.utils.utils.assignServicesFromObject(entity.consumption || [])
        entity = await window.api.utils.utils.assignPersonsFromObject(entity)
        return entity
    }

    async function newEntity() {
        const entity = new Appointment()
        if (!person && personId) {
            entity.personId = person?.personId || personId
            // a função grava entity.person e devolve o próprio entity — a pessoa sai de entity.person,
            // não do retorno (senão `person` vira o agendamento e o nome fica undefined)
            await window.api.utils.utils.assignPersonsFromObject(entity)
            person = entity.person
        }
        if (entities.length === 0) {
            // nunca `new Date(dates.end)` direto: término ausente viraria 1970, não data inválida
            const intervalo = intervaloInicial(dates)
            entity.start = intervalo.start
            entity.end = intervalo.end
        } else {
            entity.start = entities[entities.length - 1].start
            entity.end = entities[entities.length - 1].end
        }
        entity.userId = window.store.token.userId
        entity.scheduleId = dates.scheduleId
        entity.status = AppointmentStatus.agendamento_criado
        entity.items = []
        entity.resources = []
        entity.repeat = new AppointmentRepeat()
        entity.notes = ''
        if (dates) entity.auto = dates.auto

        entities = [...entities, entity]
        // o snapshot vem antes dos serviços recebidos de fora, para o botão salvar já nascer habilitado
        snapshots = [...snapshots, { snapshot: JSON.stringify(entity), isDirty: false }]

        if (dates.items?.length > 0 && entities.length === 1) {
            for (const item of dates.items) {
                entity.items.push({
                    userId: item.userId,
                    serviceId: item.serviceId,
                    parentServicesOnInvoicesId: item.parentServicesOnInvoicesId,
                } as any)
            }
            entity.items = await window.api.utils.utils.assignUsersFromObject(entity.items)
            entity.items = await window.api.utils.utils.assignServicesFromObject(entity.items, undefined, undefined, true)
            // mesmo preenchimento automático de consumo (BOM) de quando o serviço é escolhido na tela
            const autoConsumption = await window.api.utils.utils.buildConsumptionFromItems(entity.items)
            if (autoConsumption.length > 0) entity.consumption = [...(entity.consumption || []), ...autoConsumption]
            entities = entities
        }
    }

    // ---- gravação ----

    function buildDto(entity: Appointment) {
        return {
            auto: entity.auto,
            end: entity.end,
            items: entity.items.filter((el) => !!el),
            resources: (entity.resources || []).filter((el) => !!el),
            consumption: (entity.consumption || []).filter((el) => !!el),
            personId: person?.personId,
            scheduleId: entity.scheduleId,
            start: entity.start,
            status: entity.status,
            notes: entity.notes || '',
            summary: person?.name + (entity.notes ? ' / ' + entity.notes : ''),
            comissionSource,
        }
    }

    async function validateEntity(entity: Appointment) {
        const dto = buildDto(entity)
        if (!validateDto(dto)) return false
        if (!(await checkResourcesAvailability(entity))) return false
        if (await checkRequiredDocument(entity)) return false
        return true
    }

    async function createEntity(entity: Appointment, index: number, fitBlock: boolean) {
        try {
            const dto: CreateAppointmentDto = {
                ...buildDto(entity),
                repeat: entity.repeat,
                source: 'erp',
                userId: entity.userId,
            } as any
            const result = await window.api.schedulers.appointments.create(dto as any, { fitBlock })
            entities[index] = await assignObjects(result)
            window.api.users.statistics.tick('scheduler.create')
        } catch (error) {
            if (error.statusCode == 409) {
                if (!confirm('Já existe uma pessoa agendada neste horário. Deseja encaixar?')) return
                return await createEntity(entity, index, false)
            }
            evaluateError(error)
        }
    }

    async function updateEntity(entity: Appointment, index: number) {
        try {
            const dto: UpdateAppointmentDto = buildDto(entity) as any
            const result = await window.api.schedulers.appointments.update(entity.appointmentId, dto as any)
            entities[index] = await assignObjects(result)
        } catch (error) {
            evaluateError(error)
        }
    }

    const rangesOverlap = (a: { start: Date; end: Date }, b: { start: Date; end: Date }) =>
        new Date(a.start).getTime() < new Date(b.end).getTime() && new Date(a.end).getTime() > new Date(b.start).getTime()

    // última barreira antes de salvar: a lista de ocupados exibida pode estar velha, e o mesmo recurso
    // pode estar em dois agendamentos desta folha que se sobrepõem (nenhum salvo ainda)
    async function checkResourcesAvailability(entity: Appointment) {
        const resources = (entity.resources || []).filter((el) => !!el)
        if (resources.length === 0) return true

        let lockedIds: string[] = []
        try {
            lockedIds = await window.api.schedulers.resourcesLock.lockedIds(entity.start, entity.end, entity.appointmentId)
        } catch (error) {
            evaluateError('Não foi possível conferir se a sala/equipamento está livre neste horário. Tente novamente.')
            return false
        }

        // conflito que já existia antes desta edição não trava o restante (status, anotação, serviços)
        const index = entities.indexOf(entity)
        const persisted = entity.appointmentId && snapshots[index] ? JSON.parse(snapshots[index].snapshot) : null
        const keptAsPersisted = (resourcesLockId: string) =>
            !!persisted &&
            new Date(persisted.start).getTime() === new Date(entity.start).getTime() &&
            new Date(persisted.end).getTime() === new Date(entity.end).getTime() &&
            (persisted.resources || []).some((el) => el?.resourcesLockId === resourcesLockId)

        const conflicts = resources.filter((resource) => {
            const inOtherAppointment = lockedIds.includes(resource.resourcesLockId) && !keptAsPersisted(resource.resourcesLockId)
            const inThisSheet = entities.some(
                (other) =>
                    other !== entity && rangesOverlap(other, entity) && (other.resources || []).some((el) => el?.resourcesLockId === resource.resourcesLockId),
            )
            return inOtherAppointment || inThisSheet
        })
        if (conflicts.length === 0) return true

        const labels = conflicts.map((el) => el.resourceLock?.label || 'sala/equipamento').join(', ')
        evaluateError(
            conflicts.length === 1
                ? `Não é possível salvar: ${labels} já está em uso por outro agendamento neste horário.`
                : `Não é possível salvar: ${labels} já estão em uso por outros agendamentos neste horário.`,
        )
        return false
    }

    function validateDto(dto) {
        if (!dto.personId) {
            evaluateError('Selecione o cliente')
            return false
        }
        if (dto.items.length === 0) {
            evaluateError('Informe ao menos um serviço')
            return false
        }
        // horário vazio ou incompleto: a comparação abaixo seria falsa (null vira 0, data inválida vira
        // NaN) e o agendamento seguiria para o servidor, onde `new Date(null)` o grava em 1970
        if (!dto.start || !dto.end || isNaN(new Date(dto.start).getTime()) || isNaN(new Date(dto.end).getTime())) {
            evaluateError('Informe o horário de início e de término.')
            return false
        }
        if (dto.start > dto.end) {
            evaluateError('A hora final não pode ser anterior à hora inicial.')
            return false
        }
        if (dayjs(dto.start).diff(dto.end, 'second') === 0) {
            evaluateError('O horário de início e término não podem ser iguais.')
            return false
        }
        for (const item of dto.items) {
            if (item.service?.needResource && dto.resources.length === 0) {
                evaluateError('Ao menos um dos serviços selecionados exige o bloqueio de sala ou equipamento.')
                return false
            }
        }
        for (const item of dto.items) {
            if (!item.userId) {
                evaluateError('Todos os itens devem possuir um atendente.')
                return false
            }
        }
        return true
    }

    async function onSaveAll() {
        let alertDate = false
        for (const entity of entities) {
            if (!(await validateEntity(entity))) return
            if (!alertDate) alertDate = entity.start < new Date()
        }
        if (alertDate && !entities[0].appointmentId) {
            if (!confirm('ATENÇÃO: a data/hora selecionada já passou.\nDeseja mesmo adicionar?')) return
        }
        show = true
        for (let index = 0; index < entities.length; index++) {
            if (entities[index].appointmentId) await updateEntity(entities[index], index)
            else await createEntity(entities[index], index, true)
            snapshots[index] = { snapshot: JSON.stringify(entities[index]), isDirty: false }
        }
        show = false
        dispatch('change')
        dispatch(
            'close',
            entities.map((el) => el.appointmentId),
        )
    }

    // ---- exclusão ----

    async function onRemove(entity: Appointment) {
        if (entity?.appointmentId) {
            if (!confirm('Deseja mesmo excluir este registro?')) return
            try {
                show = true
                await window.api.schedulers.appointments.remove(entity.appointmentId)
                removeEntityByObject(entity)
                dispatch('change')
                if (entities.length == 0) dispatch('close', [])
            } catch (error) {
                evaluateError(error)
            } finally {
                show = false
            }
        } else {
            removeEntityByObject(entity)
            if (entities.length == 0) dispatch('cancel')
        }
    }

    async function onRemoveAnother(entity: Appointment, otherId: string) {
        if (!confirm('Deseja mesmo excluir este registro?')) return
        try {
            show = true
            await window.api.schedulers.appointments.remove(otherId)
            await loadEntity(entity.appointmentId)
            dispatch('refresh')
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function removeEntityByObject(ob: Appointment) {
        const index = entities.indexOf(ob)
        if (index >= 0) {
            entities.splice(index, 1)
            snapshots.splice(index, 1)
        }
        entities = entities
        snapshots = snapshots
    }

    function evaluateError(error) {
        if (error === "Cannot read properties of undefined (reading 'personId')") error = 'Selecione ou cadastre o cliente'
        window.error(error)
    }

    // ---- estado da tela ----

    $: evaluateDirty(entities)
    function evaluateDirty(_) {
        for (let index = 0; index < entities.length; index++) {
            if (!snapshots[index]) continue
            snapshots[index].isDirty = JSON.stringify(entities[index]) !== snapshots[index].snapshot
        }
        snapshots = snapshots
    }

    $: isDirty = snapshots.some((el) => el.isDirty)
    $: isNew = !entities[0]?.appointmentId
    $: totalItems = entities.reduce((acc, el) => acc + (el.items || []).filter((item) => !!item).length, 0)
    $: totalMinutes = entities.reduce(
        (acc, el) => acc + (el.start && el.end ? Math.round((new Date(el.end).getTime() - new Date(el.start).getTime()) / 60000) : 0),
        0,
    )
    $: createdLabel = entities[0]?.createdAt ? 'criado em ' + dayjs(entities[0].createdAt).format('DD/MM') : ''

    function durationLabel(minutes: number) {
        if (minutes <= 0) return ''
        const h = Math.floor(minutes / 60)
        const m = minutes % 60
        if (h === 0) return `${m} min`
        if (m === 0) return `${h}h`
        return `${h}h${String(m).padStart(2, '0')}`
    }

    $: summary = [
        entities.length > 1 ? `${entities.length} agendamentos` : durationLabel(totalMinutes),
        `${totalItems} ${totalItems === 1 ? 'serviço' : 'serviços'}`,
        isDirty ? 'alterações não salvas' : '',
    ]
        .filter(Boolean)
        .join(' · ')

    $: personInitials = (person?.name || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((el) => el[0])
        .join('')

    $: if (person) loadInvoices(person.personId)
    else noPaid = 0

    function personChanged() {
        for (const entity of entities) entity.personId = person?.personId
        entities = entities
    }

    onMount(async () => {
        comissionSource = await window.flags('schedulerComission')
        await loadSchedules()
        if (appointmentId) await loadEntity(appointmentId)
        else await newEntity()
    })
</script>

<LoadSpinner {show} />

{#if pendingDocuments.length > 0}
    <Popover title="Documento obrigatório" width="760px" height="50px" on:close={() => (pendingDocuments = [])}>
        <AppointmentFormRequiredDocuments lastEmissions={pendingDocuments}></AppointmentFormRequiredDocuments>
    </Popover>
{/if}

<Zsheet>
    {#if createdLabel}
        <div class="meta">{createdLabel}</div>
    {/if}

    <!-- CLIENTE -->
    <ZsheetSection label="Cliente" meta={person ? '' : 'obrigatório'}>
        <!-- personId, e não person: ao tocar em "+" para cadastrar, o seletor preenche `person` com uma
             pessoa em branco (ainda sem id) e abre o formulário dentro dele. Testando só `person`, o
             seletor saía da tela nesse instante e levava o formulário junto, sem nada abrir. -->
        {#if person?.personId}
            <ZsheetEntity
                initials={personInitials}
                icon={faUser}
                title={person.name}
                subtitle={person.phone ? window.api.utils.utils.phoneFriendly(person.phone) : ''}
                removable={isNew}
                removeTitle="Trocar cliente"
                on:remove={() => {
                    person = null
                    personChanged()
                }}
            />
            {#if window.store.token.permissions.scheduler.scheduler.finances && (noPaid > 0.1 || noPaid < -0.1)}
                <div class="balance" class:positive={noPaid < -0.1}>
                    {noPaid > 0.1 ? `Saldo em aberto de ${noPaid.toFixed(2)}` : `Saldo positivo de ${(-noPaid).toFixed(2)}`}
                </div>
            {/if}
        {:else}
            <!-- o rótulo faz as vezes de instrução enquanto o campo está vazio; o "+" ao lado cadastra -->
            <ZentitySelect fonte="pessoa" label="Buscar por nome, telefone ou e-mail" bind:value={person} on:change={personChanged}></ZentitySelect>
        {/if}
    </ZsheetSection>

    {#each entities as entity, i (entity)}
        <div class="entity" transition:slide={{ duration: 160 }}>
            {#if entities.length > 1}
                <div class="entity_sep">
                    <span class="entity_sep_line"></span>
                    <span class="entity_sep_tag">{i + 1}º agendamento{i > 0 ? ' · em paralelo' : ''}</span>
                    <span class="entity_sep_line"></span>
                </div>
            {/if}
            <AppointmentCard
                bind:entity
                {schedules}
                personId={person?.personId}
                on:change={() => (entities = entities)}
                on:state={(ev) => (state = ev.detail)}
                on:remove={() => onRemove(entity)}
                on:editAnother={(ev) => loadEntity(ev.detail)}
                on:removeAnother={(ev) => onRemoveAnother(entity, ev.detail)}
            />
        </div>
    {/each}

    {#if window.store.token.permissions.scheduler.scheduler.write}
        <button type="button" class="add-entity" on:click={newEntity}>
            <Fa icon={faPlus} /> Outro serviço em paralelo
        </button>
    {/if}

    <svelte:fragment slot="footer">
        <div class="footer">
            <div class="footer_summary">{summary}</div>
            <button type="button" class="footer_save" disabled={!isDirty || state === 'typing'} on:click={onSaveAll}>
                {isNew ? 'Salvar agendamento' : 'Salvar alterações'}
            </button>
        </div>
    </svelte:fragment>
</Zsheet>

<style>
    /* valores base = tema escuro */
    .meta {
        font-size: 12px;
        opacity: 0.5;
        padding: 0px 2px;
    }
    .entity {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    .entity_sep {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 2px 0px;
    }
    .entity_sep_line {
        flex: 1 1 auto;
        height: 1px;
        background: #ffffff1a;
    }
    .entity_sep_tag {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        opacity: 0.45;
    }
    .balance {
        margin-top: 8px;
        font-size: 13px;
        padding: 8px 10px;
        border-radius: 10px;
        background: #e0a83a1f;
        color: #e8bd6a;
    }
    .balance.positive {
        background: #2ecc711f;
        color: #7fd8a4;
    }
    .add-entity {
        border: 1px dashed #ffffff33;
        background: none;
        color: inherit;
        font: inherit;
        font-size: 14px;
        border-radius: 12px;
        padding: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
    }
    .footer {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .footer_summary {
        font-size: 12.5px;
        opacity: 0.6;
        text-align: center;
    }
    .footer_save {
        width: 100%;
        border: 0px;
        border-radius: 12px;
        background: #2ecc71;
        color: #10331f;
        font: inherit;
        font-size: 16px;
        font-weight: 700;
        padding: 14px;
    }
    .footer_save:disabled {
        opacity: 0.4;
    }

    :global(.theme_light) .entity_sep_line {
        background: #dfe2e7;
    }
    :global(.theme_light) .add-entity {
        border-color: #d8dbe0;
    }
    :global(.theme_light) .footer_save {
        color: #ffffff;
    }
    :global(.theme_light) .balance {
        background: #fff4e0;
        color: #9a6b12;
    }
    :global(.theme_light) .balance.positive {
        background: #e9f8ef;
        color: #1c7a45;
    }
</style>
